import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { catchError, switchMap, tap } from 'rxjs/operators';
import { BasicInfoService } from 'src/app/services/basic-info.service';
import { LeadApiService } from 'src/app/services/api/lead-api.service';
import { GetLeadResponseModel } from 'src/app/models/lead.model';
import { PersonalInfoService } from 'src/app/services/personal-info.service';
import { ApplicationService } from 'src/app/services/application-service';
import { CompanyApiService } from 'src/app/services/api/company-api.service';
import { TrackingService } from 'src/app/services/tracking.service';
import { FlowService } from 'src/app/services/flow.service';
import { Observable, of, Subscription } from 'rxjs';
import { Countries, Flows } from 'src/app/app.consts';
import { environment } from 'src/environments/local/environment';
import { CompanyLookupResponseModel } from 'src/app/models/company.model';

interface ClientData {
  amount: number;
  duration: number;
  phoneNumber: string;
  companyName: string;
  email: string;
  reason: string;
}

@Component({
  selector: 'app-tmidentify',
  templateUrl: './tmidentify.component.html',
  styleUrls: ['./tmidentify.component.scss'],
})
export class TmidentifyComponent implements OnInit, OnDestroy {
  subs = Array<Subscription>();
  error = '';
  isLoading = false;
  success = false;
  tosLink = environment.tos;
  rawLeadData$!: Observable<GetLeadResponseModel>;
  presentableClientData$!: Observable<ClientData>;

  constructor(
    private basicInfoService: BasicInfoService,
    public translateService: TranslateService,
    private route: ActivatedRoute,
    private leadApi: LeadApiService,
    private personalInfoService: PersonalInfoService,
    private applicationService: ApplicationService,
    public flowService: FlowService,
    private trackingService: TrackingService,
    private router: Router,
    private companyApi: CompanyApiService
  ) {}

  ngOnInit(): void {
    this.isLoading = true;

    if (
      environment.country === Countries.FINLAND ||
      environment.country === Countries.NETHERLANDS
    ) {
      this.router.navigate(['basic-info']);
    }

    this.rawLeadData$ = this.route.queryParams.pipe(
      switchMap((params) => {
        return this.leadApi.getLead(params.leadId);
      }),
      catchError((_err) => {
        this.handleErrors('Incorrect params');
        throw 'Incorrect params';
      }),
      tap((resp: GetLeadResponseModel) => {
        this.presentableClientData$ = of(this.toClientData(resp));
      }),
      catchError((_err) => {
        this.handleErrors();
        throw 'Missing data';
      })
    );

    this.subs.push(
      this.rawLeadData$
        .pipe(
          switchMap((resp: GetLeadResponseModel) => {
            this.personalInfoService.populatePersonalInfoWithGetLeadResponseData(
              resp
            );
            this.basicInfoService.populateServiceWithGetLeadResponseData(resp);
            this.applicationService.populateServiceWithGetLeadResponseData(
              resp
            );
            this.trackingService.populateCookiesWithGetLeadResponseData(
              resp,
              'tmidentify'
            );
            return this.companyApi.companyLookup(resp.orgNumber!);
          }),
          tap((lookUp: CompanyLookupResponseModel) => {
            this.basicInfoService.company = lookUp;
          })
        )
        .subscribe(
          () => {
            this.flowService.selectFlow(Flows.TM_AUTH);
            this.success = true;
            this.isLoading = false;
          },
          (_error: any) => {
            this.handleErrors();
            this.isLoading = false;
          }
        )
    );
    this.trackPageView();
  }

  toClientData(data: GetLeadResponseModel): ClientData {
    // If something is missing from the application, stop and let customer know to start over
    if (
      !data.amount ||
      !data.duration ||
      !data.email ||
      !data.phoneNumber ||
      !data.companyName ||
      !data.reason
    ) {
      this.success = false;
      this.handleErrors();
    }

    return {
      amount: data.amount!,
      duration: data.duration!,
      email: data.email,
      phoneNumber: data.phoneNumber,
      companyName: data.companyName!,
      reason: data.reason!,
    };
  }

  trackPageView(): void {
    this.trackingService.trackPageView('tmidentify');
  }

  buttonNext(): void {
    this.flowService.nextScreen();
  }

  restartFlow(): void {
    this.router.navigate(['basic-info']);
  }

  handleErrors(incorrectQueryParam?: string): void {
    if (incorrectQueryParam === 'Incorrect params')
      location.href = `${environment.creddoPage}`;
    this.error = `${this.translateService.instant('tmidentify.error')}`;
  }

  ngOnDestroy(): void {
    this.subs.map((s) => s.unsubscribe());
  }
}
